<template>
  <b-modal
    v-model="show_"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    @hide="onModalHide"
  >
    <template slot="modal-title">
      <i class="icon-file-check mr-2" />
      {{ $t(`${tt}.modal_title`) }}
    </template>

    <div v-show="!preview.length">
      <div class="mb-3 tab-pane show fade active">
        <div class="card-body p-0">
          <p>{{ $t(`${tt}.modal_description`) }}</p>
          <h6>{{ $t(`${tt}.file_title`) }}</h6>
          <div class="form-group">
            <label>{{ $t(`${tt}.file_label`) }}</label>
            <b-form-file
              id="file-small"
              placeholder="Choose profile"
              accept="image/*"
              @change="onFileUpdate"
            />
            <span class="form-text text-muted">{{ $t(`${tt}.file_description`) }}</span>
          </div>
          <h6>{{ $t(`${tt}.generate_title`) }}</h6>
          <button
            class="btn btn-success"
            type="button"
            @click="onGenerate"
          >
            {{ $t(`${tt}.btn_generate`) }}
          </button>
        </div>
      </div>

      <div
        class="d-flex justify-content-center"
        v-html="identIcon.data"
      />

      <div v-show="imgSrc && restriction.aspectRatio">
        <vue-cropper
          v-if="restriction.aspectRatio"
          ref="cropper"
          :aspect-ratio="restriction.aspectRatio"
          :zoomable="false"
          :min-crop-box-width="150"
          :min-crop-box-height="150/restriction.aspectRatio"
          :cropstart="onCropperStart"
          :cropend="onCropperEnd"
        />
      </div>
      <widget-loader v-if="restriction.loading" />
    </div>

    <div v-show="preview.length">
      <div class="text-center">
        <div class="h6">
          {{ $t(`${tt}.saving_changes`) }}
        </div>
        <img
          v-if="restriction.aspectRatio > 0"
          :src="preview"
          :width="200"
          :height="200/restriction.aspectRatio"
          alt="preview"
        >
      </div>
    </div>

    <div
      slot="modal-footer"
      class="w-100 text-right"
    >
      <button
        :disabled="submit"
        class="btn ml-auto btn-primary btn-labeled btn-labeled-left"
        type="button"
        @click="onSubmit"
      >
        <b><i :class="[submit ? 'icon-spinner3 spinner' : 'icon-checkmark4']" /></b>
        {{ $t(`${tt}.submit`) }}
      </button>
    </div>
  </b-modal>
</template>

<script>
import axios from "axios";
import VueCropper from 'vue-cropperjs';
import {toSvg} from 'jdenticon';
import {svgAsPngUri} from 'save-svg-as-png';
import catchDispatch, {catchDispatchErrorMsg} from "@/utils/catchDispatch";
import makeid from "@/utils/makeid";
import dataURLToBlob from "@/utils/dataURLToBlob";
import ModalShowMixins from "@/mixins/ModalShowMixins";
import WidgetLoader from "@/components/Widget/WidgetLoader";
import {mapGetters} from "vuex";

export default {
  components: {
    WidgetLoader,
    VueCropper,
  },
  mixins: [
    ModalShowMixins,
  ],
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    tt: 'tabs.settings.change_profile.form.avatar_image_url.modal',
    submit: false,
    restriction: {
      loading: true,
      name: 'avatar_user',
      mimes: [],
      aspectRatio: 0,
      width: 600,
      sizeHeight: 600,
    },
    imgSrc: false,
    isCropperProcessing: false,
    preview: '',
    identIcon: {
      text: '',
      data: '',
    },
    form: {
      submit: false,
      sendFile: '',
      sendFileUrl: '',
    },
  }),
  computed: {
    ...mapGetters('auth', ['user']),
  },
  watch: {
    show(show) {
      if (!show) return;

      this.imgSrc = false;
      this.isCropperProcessing = false;
      this.preview = '';
      this.identIcon = {
        text: '',
        data: '',
      };
      this.form = {
        submit: false,
        sendFile: '',
        sendFileUrl: '',
      };
      this.getRestriction();
    },
  },
  methods: {
    onModalHide(e) {
      if (this.isCropperProcessing) {
        e.preventDefault();
        return false;
      }
    },
    onCropperStart() {
      this.isCropperProcessing = true;
    },
    onCropperEnd() {
      setTimeout(() => this.isCropperProcessing = false, 300);
    },
    async getRestriction() {
      console.info('AvatarFormChangeProfileProfileTabComponents.getRestriction');

      if (this.restriction.mimes.length > 0) return;

      try {
        const {name} = this.restriction;
        this.restriction.loading = true;
        const r = await axios
          .get(`/images/api/v1/restrictions`, {params: {page: 1, page_size: 1, name}})
          .then(({data: {Body}}) => {
            const restriction = (Body || []).find(r => r.name === name);
            if (!restriction) {
              throw 'Restrictions ' + name + ' not found';
            }
            return restriction;
          });

        this.restriction.aspectRatio = r.preview.width / r.preview.height;
        this.restriction.mimes = r.mimes;
        this.restriction.width = (r.size['max-width'] > 0) ? r.size['max-width'] : 600;
        this.restriction.height = (r.size['max-height'] > 0) ? r.size['max-height'] : 600;
      } catch (e) {
        catchDispatch.call(this, e);
        setTimeout(() => this.show_ = false, 800);
      } finally {
        this.restriction.loading = false;
      }
    },
    onFileUpdate(e) {
      console.info('AvatarFormChangeProfileProfileTabComponents.onFileUpdate');

      const file = e.target.files[0];

      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = () => {
        try {
          if (!/^image\//.test(file.type)) {
            throw this.$t(`${this.tt}.error_file_type`);
          }
        } catch (e) {
          catchDispatch.call(this, e);
          return;
        }

        this.imgSrc = true;
        this.identIcon.data = '';
        this.$refs.cropper.replace(`data:${file.type};base64,${btoa(reader.result)}`);
      };
      reader.onerror = (e) => {
        console.error('AvatarFormChangeProfileProfileTabComponents.onFileUpdate | onerror | ', e);
        catchDispatch.call(this, e);
      };
    },
    onGenerate() {
      console.info('AvatarFormChangeProfileProfileTabComponents.onGenerate');

      this.identIcon.text = makeid(32);
      this.identIcon.data = toSvg(this.identIcon.text, 400);
      this.imgSrc = false;
    },
    onSubmit() {
      console.info('AvatarFormChangeProfileProfileTabComponents.onSubmit');

      if (this.imgSrc) {
        this.onSubmitImg.apply(this, arguments);
      } else {
        this.onSubmitIdentIcon.apply(this, arguments);
      }
    },
    onSubmitImg() {
      console.info('AvatarFormChangeProfileProfileTabComponents.onSubmitImg');

      if (this.submit) return;
      this.submit = true;

      const {width, height} = this.restriction;
      const getCroppedCanvasOpt = {width, height};
      const preview = this.$refs.cropper.getCroppedCanvas(getCroppedCanvasOpt).toDataURL('image/jpeg');
      this.onSendFile(preview);
    },
    onSubmitIdentIcon() {
      console.info('AvatarFormChangeProfileProfileTabComponents.onSubmitIdentIcon');

      if (this.submit) return;
      this.submit = true;

      const canvas = document.createElement('div');
      canvas.innerHTML = toSvg(this.identIcon.text, 400);
      const svg = canvas.getElementsByTagName('svg')[0];
      svgAsPngUri(svg, {}).then(preview => this.onSendFile(preview));
    },
    async onSendFile(preview) {
      this.preview = preview;
      this.form.sendFile = dataURLToBlob(preview);
      try {
        await this.onSendFileUploadImage();
        await this.onSendFileAvatarUpdate();
      } catch (e) {
        console.error('AvatarFormChangeProfileProfileTabComponents.onSendFile');

        const eMsg = catchDispatchErrorMsg.call(this, e).toLowerCase();
        if (eMsg.includes('messaggio_otp')) {
          this.$otp({name: `${this.tt}.widget_otp_name`}, this.onSendFileAvatarUpdate);
          return;
        }

        catchDispatch.call(this, e);
      } finally {
        this.submit = false;
      }
    },
    async onSendFileUploadImage() {
      const fileForm = new FormData();
      fileForm.append('restriction', this.restriction.name);
      fileForm.append('file', this.form.sendFile);

      return axios
        .post(`/images/api/v1/images`, fileForm)
        .then(({data: {url}}) => url + '_small')
        .then(url => {
          this.form.sendFileUrl = url;
          this.$emit('input', url);
        });
    },
    onSendFileAvatarUpdate({confirmation} = {}) {
      return axios.put(
        `/entities/api/v1/users/${this.user.id}/avatar/update`,
        {avatar_image_url: this.form.sendFileUrl},
        {headers: {'Messaggio-OTP': confirmation}},
      ).then(({data}) => {
        this.$store.commit('auth/SET_USER', {
          ...this.$store.getters['auth/user'],
          ...data,
        });
        this.show_ = false;
      });
    },
  },
};
</script>

<style scoped>

</style>
